import { LoaderCircle, LockKeyhole, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api } from "./api.ts";
import { useLocale } from "./i18n.tsx";
import { Page, type Session } from "./Shell.tsx";

type Access = { allowed: boolean; loginRequired: boolean; loginUrl: string | null };

const TEXT = {
  "zh-CN": {
    loading: "正在确认你的 Plus 权益…", eyebrow: "NeckMoves Plus", title: "一次订阅，全部进阶游戏都能玩",
    body: "六款 Plus 游戏，一次解锁：出海巡航、林间送信、海底拍照，或弹一首小夜曲。核心一分钟游戏继续免费开放。",
    login: "登录并继续", upgrade: "查看 Plus 订阅", retry: "重新确认权益", unavailable: "暂时无法确认订阅状态，请稍后再试。",
  },
  en: {
    loading: "Checking your Plus access…", eyebrow: "NeckMoves Plus", title: "One subscription unlocks every extended game",
    body: "Six Plus games, one subscription: sail, deliver woodland letters, take reef portraits or play a little nocturne. The one-minute core games stay free.",
    login: "Log in to continue", upgrade: "View Plus subscription", retry: "Check access again", unavailable: "We couldn't confirm your subscription just now. Please try again.",
  },
} as const;

export function PlusGameGate({ session, children }: { session: Session | null; children: ReactNode }) {
  const { locale, href } = useLocale();
  const text = TEXT[locale];
  const [access, setAccess] = useState<Access | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    setAccess(null);
    void api<Access>("/api/billing/more-games-access").then(setAccess).catch(() => setFailed(true));
  }, []);

  useEffect(load, [load, session?.id]);

  if (access?.allowed) return <>{children}</>;
  return (
    <Page session={session} className="plus-gate-page">
      <main id="main" className="plus-gate-shell page-shell">
        <section className="plus-gate-card">
          <div className="plus-gate-art" aria-hidden="true">{failed ? <LockKeyhole /> : access ? <Sparkles /> : <LoaderCircle className="spinner" />}</div>
          {failed ? <><p className="eyebrow">{text.eyebrow}</p><h1>{text.unavailable}</h1><button className="button button-primary" type="button" onClick={load}>{text.retry}</button></>
            : !access ? <p role="status">{text.loading}</p>
              : <><p className="eyebrow">{text.eyebrow}</p><h1>{text.title}</h1><p>{text.body}</p><a className="button button-primary" href={access.loginRequired ? access.loginUrl || href("/login") : href("/pricing")}>{access.loginRequired ? text.login : text.upgrade}</a><a className="button button-quiet" href={href("/play")}>{locale === "en" ? "Choose a free game" : "选择免费游戏"}</a></>}
        </section>
      </main>
    </Page>
  );
}
